import { Alert } from '@/global/components/Alert'
import { Link } from '@/global/components/Link'
import Grid from '@/global/components/ui/Grid'
import { hasPermission } from '@/hooks/useBackendMenu'
import { SN } from '@acme/core'
import { Divider } from '@mui/material'
import { FormField, FormFieldCheckbox } from 'global/components/Form'
import IconeAjuda from 'global/components/IconeAjuda'
import { useFormContext } from 'react-hook-form'
import HorasExtrasPart from './HorasExtras'
import FormHoristaFaltas from './HoristaFaltas'
import FormNoturnoRegraCalculo from './Noturno'
import PorcentagemPart from './Porcentagem'

export function TabGeral() {
    const { watch, setValue } = useFormContext()

    const compensacaoManual = watch('compensacaoManual')
    const bancoHoras = watch('controlarBancoHoras')
    const controlaBancoHoras = SN.isTrue(bancoHoras)
    const hrsExtrasNoturnas = watch('hrsExtrasNoturnas')

    return (
        <Grid container className="pb-8">
            <Grid item xs={12}>
                <Divider>Dados básicos</Divider>
            </Grid>
            <Grid item xs={12}>
                <FormField
                    name="nome"
                    label="Nome da regra de cálculo"
                    fullWidth
                    autoComplete="off"
                />
            </Grid>

            <Grid item xs={12}>
                <Divider>Banco de horas</Divider>
            </Grid>
            <Grid item xs={12}>
                <div className="flex items-center w-full">
                    <FormFieldCheckbox
                        name="controlarBancoHoras"
                        label="Controla banco de horas"
                        disabled={compensacaoManual}
                        onChange={(_, checked) => {
                            if (checked) setValue('compensacaoFifo', false)
                        }}
                    />{' '}
                    <IconeAjuda>
                        Caso habilitado, o saldo de horas do colaborador será
                        zerado apenas no fechamento do banco de horas.
                        <br />
                        <br />O período de fechamento do banco de horas é
                        configurado na tela de configurações da empresa.
                        <br />
                        <br />
                        <strong>Atenção:</strong> Esta opção não pode ser
                        habilitada junto com a compensação manual.
                    </IconeAjuda>
                </div>
            </Grid>
            <Grid item sm={6} xs={12}>
                <FormField
                    name="inicioBancoHoras"
                    label="Início do banco de horas"
                    disabled={!controlaBancoHoras}
                    type="month"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        endAdornment: (
                            <IconeAjuda>
                                Competência a partir da qual o banco de horas
                                passa a valer. O mês informado já acumula.
                                <br />
                                <br />
                                Deixe em branco se o banco sempre valeu.
                            </IconeAjuda>
                        ),
                    }}
                    helperText="Deixe em branco para indeterminado"
                />
            </Grid>
            <Grid item sm={6} xs={12}>
                <FormField
                    name="fimBancoHoras"
                    label="Fim do banco de horas"
                    type="month"
                    disabled={!controlaBancoHoras}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        endAdornment: (
                            <IconeAjuda>
                                Última competência em que o banco de horas vale.
                                O mês informado ainda acumula; o seguinte já
                                não.
                                <br />
                                <br />O saldo remanescente não é pago
                                automaticamente — use o pagamento de horas na
                                competência do fim.
                            </IconeAjuda>
                        ),
                    }}
                />
            </Grid>

            <Grid item xs={12} className="mt-2">
                <Divider>Compensações</Divider>
            </Grid>

            <Grid item sm={6} xs={12}>
                <div className="flex items-center w-full">
                    <FormFieldCheckbox
                        name="compensacaoDeHoras"
                        label="Compensação diária"
                    />{' '}
                    <IconeAjuda>
                        Caso habilitado, as faltas e horas extras que ocorrerem
                        no mesmo dia serão compensadas e o dia nunca vai
                        apresentar faltas e horas extras ao mesmo tempo.
                        <br />
                        <br />
                        Para controlar as compensações entre vários dias,
                        configure a porcentagem da regra de cálculo.
                    </IconeAjuda>
                </div>
            </Grid>
            <Grid item sm={6} xs={12}>
                <div className="flex items-center w-full">
                    <FormFieldCheckbox
                        name="compensacaoManual"
                        label="Compensação manual"
                        disabled={controlaBancoHoras}
                    />{' '}
                    <IconeAjuda>
                        Caso habilitado, o sistema irá compensar manualmente as
                        horas do colaborador.
                        <br />
                        <br />
                        Se a sua empresa não faz o controle de compensações
                        manuais, mantenha a opção desabilitada.
                        <br />
                        <br />
                        <strong>Atenção:</strong> Esta opção não pode ser
                        habilitada junto com o controle de banco de horas.
                    </IconeAjuda>
                </div>
            </Grid>
            <Grid item sm={6} xs={12}>
                <div className="flex items-center -gap-2 w-full">
                    <FormFieldCheckbox
                        name="compensarJustificadas"
                        label="Compensar faltas justificadas"
                    />
                    <IconeAjuda>
                        Caso desabilitado, o sistema não irá compensar faltas
                        justificadas, utilize esta opção caso sua empresa faz a
                        separação entre faltas justificadas e injustificadas.
                    </IconeAjuda>
                </div>
            </Grid>
            <Grid item sm={6} xs={12}>
                <div className="flex items-center w-full">
                    <FormFieldCheckbox
                        name="compensarInjustificadas"
                        label="Compensar faltas injustificadas"
                    />
                    <IconeAjuda>
                        Caso desabilitado, o sistema não irá compensar faltas
                        injustificadas, utilize esta opção caso sua empresa faz
                        a separação entre faltas justificadas e injustificadas.
                    </IconeAjuda>
                </div>
            </Grid>
            <Grid item sm={6} xs={12}>
                <div className="flex items-center w-full">
                    <FormFieldCheckbox
                        name="hrsExtrasNoturnas"
                        label="Calcular horas extras noturnas"
                        onChange={(_, checked) => {
                            if (!checked) {
                                setValue('compensarHrsExtrasNoturnas', false)
                            }
                        }}
                    />
                    <IconeAjuda>
                        Caso habilitado, o sistema irá calcular e registrar
                        separadamente as horas extras realizadas no período
                        noturno (entre o início e fim do horário noturno
                        configurado na empresa).
                    </IconeAjuda>
                </div>
            </Grid>
            <Grid item sm={6} xs={12}>
                <div className="flex items-center w-full">
                    <FormFieldCheckbox
                        name="compensarHrsExtrasNoturnas"
                        label="Compensar horas extras noturnas"
                        disabled={!hrsExtrasNoturnas}
                    />
                    <IconeAjuda>
                        Caso habilitado, as horas extras noturnas serão
                        compensadas com faltas no banco de horas. Caso
                        desabilitado, serão apenas exportadas para a folha de
                        pagamento.
                        <br />
                        <br />
                        Só pode ser habilitado junto com{' '}
                        <b>Calcular horas extras noturnas</b>
                    </IconeAjuda>
                </div>
            </Grid>
            <Grid item sm={6} xs={12}>
                <div className="flex items-center w-full">
                    <FormFieldCheckbox
                        name="toleranciaCLT"
                        label="Tolerância CLT"
                    />
                    <IconeAjuda>
                        Caso habilitado, o sistema irá considerar a tolerância
                        conforme a CLT para os registros de ponto, independente
                        da configuração de tabela de horário.
                        <br />
                        <br />O parágrafo primeiro do art. 58 da CLT cita que:
                        "não serão descontadas nem computadas como jornada
                        extraordinária as variações de horário no registro de
                        ponto não excedentes de cinco minutos, observado o
                        limite máximo de dez minutos diários."
                    </IconeAjuda>
                </div>
            </Grid>
            <Grid item sm={6} xs={6}>
                <div className="flex items-center w-full">
                    <FormFieldCheckbox
                        name="compensacaoFifo"
                        label="Compensação cronológica"
                        disabled={!bancoHoras}
                    />
                    <IconeAjuda>
                        Caso habilitado, a compensação de faltas e o pagamento
                        de horas consomem o saldo do banco na ordem cronológica
                        (o dia mais antigo primeiro — PEPS), em vez de priorizar
                        a maior ou menor porcentagem. O dia exato que compensou
                        cada saldo aparece no{' '}
                        {hasPermission(
                            ['read', 'VencimentoDoBancoDeHoras'],
                            () => (
                                <Link href="/relatorios/vencimento-banco">
                                    relatório de vencimento do banco de horas
                                </Link>
                            ),
                            () => 'relatório de vencimento do banco de horas',
                        )}
                        .
                        <br />
                        <br />
                        <strong>Atenção:</strong> disponível apenas com o
                        controle de banco de horas habilitado.
                    </IconeAjuda>
                </div>
            </Grid>

            <Grid item xs={12} className="mt-2">
                <Divider>Interjornada</Divider>
            </Grid>
            <Grid item sm={6} xs={12} className="flex items-center pt-2.5">
                <FormFieldCheckbox
                    name="calcularHorasSuprimidas"
                    label="Calcular horas suprimidas"
                />
                <IconeAjuda>
                    Caso habilitado, o sistema irá calcular e exibir as horas
                    suprimidas no espelho de ponto e relatório de horas
                    suprimidas.
                    <br />
                    <br />
                    Quando desabilitado, o relatório de inconsistências ainda
                    vai exibir as horas suprimidas.
                </IconeAjuda>
            </Grid>
            <Grid item xs={6}>
                <FormField
                    name="horasInterjornada"
                    label="Horas interjornada"
                    type="time"
                    required
                    InputProps={{
                        endAdornment: (
                            <div className="inline-flex items-baseline">
                                <span>h</span>
                                <IconeAjuda>
                                    Determina o intervalo mínimo entre a jornada
                                    de trabalho de um dia para outro. Horas
                                    inferiores a configuração serão consideradas
                                    como horas suprimidas.
                                    <br />
                                    <br />A CLT determina um intervalo mínimo de
                                    11 horas entre uma jornada e outra.
                                </IconeAjuda>
                            </div>
                        ),
                    }}
                    sx={{
                        '[type="time"]::-webkit-calendar-picker-indicator': {
                            display: 'none',
                        },
                    }}
                />
            </Grid>
        </Grid>
    )
}

export function TabNoturno() {
    return <FormNoturnoRegraCalculo />
}

export function TabHorasExtras() {
    const {
        formState: { errors: formErrors },
    } = useFormContext()

    return (
        <Grid container>
            <Grid item xs={12}>
                <Divider sx={{ mt: 1, mb: 2 }}>
                    Definições de porcentagens para compensação
                </Divider>
            </Grid>
            <Grid item xs={12}>
                <ErrorAlert obj={formErrors} field="porcentagens" />
            </Grid>
            <Grid item xs={12}>
                <PorcentagemPart />
            </Grid>

            <Grid item xs={12}>
                <Divider sx={{ mt: 1, mb: 2 }}>
                    Configurações de horas extras semanais
                </Divider>
            </Grid>
            <Grid item xs={12}>
                <ErrorAlert obj={formErrors} field="horasExtras" />
            </Grid>
            <Grid item xs={12}>
                <HorasExtrasPart />
            </Grid>
        </Grid>
    )
}

export function TabHoristaEFaltas() {
    return <FormHoristaFaltas />
}

interface IErrorProps {
    obj: Record<string, any>
    field: string
}

function ErrorAlert({ obj, field }: IErrorProps) {
    if (!obj[field]) return null

    const msgRoot = obj[field].root?.message || obj[field].message?.toString()
    if (msgRoot) return <Alert severity="error">{msgRoot}</Alert>

    let msg = obj[field]
    do {
        if (!msg) return null
        if ('message' in msg) msg = String(msg.message)
        else if (Array.isArray(msg)) msg = msg.find(Boolean)
        else msg = Object.values(msg)
    } while (typeof msg !== 'string')

    return <Alert severity="error">{msg}</Alert>
}
